import { Sparkles } from "lucide-react";

import { SAMPLES, type Sample, type SampleCategory } from "@/samples";

interface Props {
  onPick: (sample: Sample) => void;
  activeId?: string;
}

const CATEGORY_STYLE: Record<
  SampleCategory,
  { label: string; chip: string; dot: string }
> = {
  "direct-match": {
    label: "Direct match",
    chip: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    dot: "bg-emerald-400",
  },
  "partial-overlap": {
    label: "Partial overlap",
    chip: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    dot: "bg-amber-400",
  },
  "cross-domain": {
    label: "Cross-domain",
    chip: "bg-sky-500/10 text-sky-300 border-sky-500/30",
    dot: "bg-sky-400",
  },
  mismatch: {
    label: "Mismatch",
    chip: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    dot: "bg-rose-400",
  },
};

export function SamplePicker({ onPick, activeId }: Props) {
  return (
    <div className="ta-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-purple-400" />
        <h3 className="text-sm font-semibold text-slate-100">Sample library</h3>
        <span className="text-xs text-slate-500 ml-auto">
          {SAMPLES.length} hand-curated pairs
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SAMPLES.map((sample) => {
          const cat = CATEGORY_STYLE[sample.category];
          const isActive = activeId === sample.id;
          return (
            <button
              key={sample.id}
              type="button"
              onClick={() => onPick(sample)}
              className={`text-left rounded-lg border px-4 py-3 transition-all ${
                isActive
                  ? "border-purple-500/60 bg-purple-500/5"
                  : "border-slate-800/60 bg-slate-900/40 hover:border-slate-700 hover:bg-slate-900/70"
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="text-sm font-medium text-slate-100 leading-tight">
                  {sample.label}
                </span>
                <span
                  className={`inline-flex items-center gap-1 flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${cat.chip}`}
                >
                  <span className={`w-1 h-1 rounded-full ${cat.dot}`} />
                  {cat.label}
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-snug line-clamp-2">
                {sample.summary}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
